/**
 * 图片切割弹窗（九宫格切割）
 *
 * 功能：
 *  - 行 × 列选择（1–5）
 *  - 预览切割网格线与每格序号
 *  - 确认后按格切割为独立图片节点，网格排布在源节点右侧并自动连线
 */
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Grid3x3, Loader2, X } from 'lucide-react';
import { splitImageToGrid } from './utils/imageDerive';
import type { CanvasNode, ImageNodeData } from './types';
import { resolveBrowserMediaUrl } from '../../utils/storageUrl';
import toast from '../../components/Toast';

interface SplitDialogProps {
  sourceNode: CanvasNode<ImageNodeData>;
  imageUrl: string;
  onClose: () => void;
}

const CANVAS_SIZE = 480;
const GRID_OPTIONS = [1, 2, 3, 4, 5];

export function SplitDialog({ sourceNode, imageUrl, onClose }: SplitDialogProps) {
  const [rows, setRows] = useState(3);
  const [cols, setCols] = useState(3);
  const [busy, setBusy] = useState(false);

  const handleSplit = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const result = await splitImageToGrid(sourceNode, imageUrl, rows, cols);
      toast.success(`已切割为 ${result.created} 个图片节点`);
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '切割失败');
      setBusy(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="flex w-[680px] max-w-[90vw] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 标题栏 */}
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
          <div className="flex items-center gap-2">
            <Grid3x3 className="h-4 w-4 text-emerald-500" />
            <h3 className="text-sm font-semibold text-slate-900">切割图片</h3>
          </div>
          <button onClick={onClose} className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* 预览区 */}
        <div className="flex items-center justify-center bg-slate-100 p-6">
          <div
            className="relative overflow-hidden rounded-lg bg-black/5"
            style={{ width: CANVAS_SIZE, height: CANVAS_SIZE }}
          >
            <img
              src={resolveBrowserMediaUrl(imageUrl)}
              alt="切割预览"
              className="absolute select-none object-contain"
              style={{ left: 0, top: 0, width: CANVAS_SIZE, height: CANVAS_SIZE, pointerEvents: 'none' }}
              draggable={false}
            />
            {/* 网格线 */}
            <div className="absolute inset-0" style={{ pointerEvents: 'none' }}>
              {Array.from({ length: cols - 1 }).map((_, i) => (
                <div
                  key={`v${i}`}
                  className="absolute top-0 h-full w-px bg-white/80"
                  style={{ left: `${((i + 1) / cols) * 100}%`, boxShadow: '0 0 0 0.5px rgba(0,0,0,0.35)' }}
                />
              ))}
              {Array.from({ length: rows - 1 }).map((_, i) => (
                <div
                  key={`h${i}`}
                  className="absolute left-0 h-px w-full bg-white/80"
                  style={{ top: `${((i + 1) / rows) * 100}%`, boxShadow: '0 0 0 0.5px rgba(0,0,0,0.35)' }}
                />
              ))}
            </div>
          </div>
        </div>

        {/* 底部控制栏 */}
        <div className="flex items-center justify-between border-t border-slate-200 px-5 py-3">
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-xs text-slate-600">
              行
              <select
                value={rows}
                onChange={(e) => setRows(Number(e.target.value))}
                className="rounded-md border border-light-border bg-white px-2 py-1 text-xs"
              >
                {GRID_OPTIONS.map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </label>
            <label className="flex items-center gap-2 text-xs text-slate-600">
              列
              <select
                value={cols}
                onChange={(e) => setCols(Number(e.target.value))}
                className="rounded-md border border-light-border bg-white px-2 py-1 text-xs"
              >
                {GRID_OPTIONS.map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </label>
            <span className="text-xs text-slate-400">将创建 {rows * cols} 个图片节点</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg px-4 py-2 text-sm text-slate-600 transition hover:bg-slate-100"
            >
              取消
            </button>
            <button
              type="button"
              onClick={handleSplit}
              disabled={busy}
              className="flex items-center gap-1.5 rounded-lg bg-emerald-500 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Grid3x3 className="h-4 w-4" />}
              {busy ? '切割中...' : '切割'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
